import find from 'lodash/find.js';

import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { initialState } from '@/Helpers.js';
import UserGroup from '@/models/UserGroup.js';

export default class GateKeeper {
    forUser(user) {
        this.user = user;

        return this;
    }

    before(user) {
        if (user.hasAnyRole(['Owner', 'Admin', 'Instruction Admin'])) {
            return true;
        }

        return null;
    }

    async userHasAccessToCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        // Corporate runs a flat content model: courses (including ones launched
        // from the course generator) carry no group association, so non-student
        // staff who can view courses may open any course. Mirrors the backend
        // Course::userHasPermissionTo() corporate bypass. Students fall through
        // to the enrollment check below so they only reach their own courses.
        // Resolve the student check against the BOUND user (this.user), not the current
        // session, so impersonation / non-session-user gate checks authorize the right
        // person. Mirrors isCorporateStudentUser()'s rule: student iff hasRole('Student')
        // AND no admin rights (Owner / Admin / Instruction Admin — the User `is_admin`
        // getter). isCorporateClient() reads environment config (not user state), so it
        // stays global.
        const isCorporateStudent = this.user.hasRole('Student') && !this.user.is_admin;

        if (isCorporateClient() && !isCorporateStudent && this.user.hasPermissionTo('view a course')) {
            return true;
        }

        if (this.user.enrollments.length > 0) {
            const courseEnrollment = find(this.user.enrollments, {
                course_id: Number.parseInt(courseId, 10),
            });

            const disabledStatuses = [
                ...initialState('AccountStatuses.disabling'),
                ...initialState('AccountStatuses.removed'),
            ];

            const hasDisabledStatus = courseEnrollment?.statuses.some(status => disabledStatuses.includes(status.name));

            if (courseEnrollment && !hasDisabledStatus) {
                return true;
            }
        }

        const userGroups = UserGroup.query().with(['role']).where('user_id', this.user.id).get();

        // Use Promise.all to check all user groups in parallel
        const accessChecks = await Promise.all(
            userGroups.map(async userGroup => {
                // Check role first
                const isNotStudent = !['Student', 'Teaching Assistant'].includes(userGroup.role?.name);

                if (!isNotStudent) {
                    return false;
                }

                // Check if group has course access using the new async method
                const hasCourseAccess = await userGroup.hasCourseAsync(Number.parseInt(courseId, 10));

                return hasCourseAccess;
            })
        );

        const hasAccess = accessChecks.some(result => result);

        return hasAccess;
    }

    userHasAccessToPreviewQuiz(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.isStudentOf(courseId)) {
            return false;
        }

        if (this.user.hasEnrolledPermissionTo('view all quizzes') && this.user.isInstructorOf(courseId)) {
            return true;
        }

        if (this.user.hasRoleWithinCourseGroup(courseId, 'Instructor')) {
            return this.user.hasGroupPermissionWithinCourse(courseId, 'view all quizzes');
        }

        return false;
    }

    userHasAccessToReport(report) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.hasPermissionTo('view reports') && this.user.hasPermissionTo(`view ${report}`)) {
            return true;
        }

        if (this.user.hasGroupPermissionTo('view reports') && this.user.hasGroupPermissionTo(`view ${report}`)) {
            return true;
        }

        if (this.user.hasEnrolledPermissionTo('view reports') && this.user.hasEnrolledPermissionTo(`view ${report}`)) {
            return true;
        }

        return false;
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param courseId
     * @returns {boolean|*}
     */
    userHasAccessToEditCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.hasRoleWithinCourseGroup(courseId, 'Content Editor')) {
            return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
        }

        if (this.user.hasPermissionTo('edit a course')) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasPermissionTo(permission);
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasGroupPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionTo(permission);
    }

    userHasGroupPermissionWithinCourse(courseId, permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(Number(courseId), permission);
    }

    userIsGroupInstructor() {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.isGroupInstructor();
    }

    /**
     * Whether the user is an instructor in any capacity (global role, course
     * enrollment, or group). Pure students return false. Used to gate the
     * instructor evaluation dashboard, which is not meant for students.
     * @returns {boolean}
     */
    userIsInstructor() {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.is_instructor || this.user.isInstructorOfActiveCourse() || this.user.isGroupInstructor();
    }

    userIsViewingOwnResource(resourceUserId) {
        return Number(this.user.id) === Number(resourceUserId);
    }

    denialMessage(to) {
        const message = to?.meta?.denialMessage;

        if (typeof message === 'string' && message.trim()) {
            return message.trim();
        }

        return `Access denied to ${to.path}`;
    }
}
